<template>
	<div>
		<div class="d-flex justify-content-between">
			<h1 class="page-header">{{ $t('reportClassAttendance.pageTitle') }}</h1>
			<ts-breadcrumb :items="[
				{ text: $t('toHomePage'), href: '/' },
				{
					text: $t('reportClassAttendance.pageTitle'),
					active: true
				}
			]" />
		</div>
		<div class="row">
			<div class="col-md-3">
				<ts-card>
					<ts-card-wrapper>
						<ts-loading-banner :loading="loading">
							<form>
								<fieldset>
									<legend>{{ $t('reportClassAttendance.criteria') }}</legend>

									<div class="row form-group">
										<div class="col-md-12">
											<label class="required">{{
												$t('reportClassAttendance.acYear')
											}}</label>
											<a-select style="width: 100%" v-model.number="model.ac_year_id"
												:options="ac_year_list" @change="acYearChange"></a-select>
											<div class="validate" v-if="errors.has('ac_year_id')">
												{{ errors.first('ac_year_id') }}
											</div>
										</div>
									</div>
									<div class="row form-group">
										<div class="col-md-12">
											<label class="required">{{
												$t('reportClassAttendance.subProgram')
											}}</label>
											<a-select style="width: 100%" v-model.number="model.acps_id"
												:options="sub_programs" :show-search="true" :filter-option="(input, option) =>
													option.componentOptions.children[0].text
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
													" @change="onSubChange" :disabled="!model.ac_year_id"></a-select>
											<div class="validate" v-if="errors.has('acps_id')">
												{{ errors.first('acps_id') }}
											</div>
										</div>
									</div>
									<div class="row form-group">
										<div class="col-md-12">
											<label class="required">{{
												$t('reportClassAttendance.acTerm')
											}}</label>
											<a-select style="width: 100%" v-model.number="model.ac_term_id"
												:options="ac_terms" :show-search="true" :filter-option="(input, option) =>
													option.componentOptions.children[0].text
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
													" @change="acTermChange"></a-select>
											<div class="validate" v-if="errors.has('ac_term_id')">
												{{ errors.first('ac_term_id') }}
											</div>
										</div>
									</div>
									<div class="row form-group">
										<div class="col-md-12">
											<label class="required">{{
												$t('reportClassAttendance.date')
											}}</label>
											<a-range-picker format="DD-MM-YYYY" v-model="rangeDate"
												:disabled-date="disabledDate" @calendarChange="onCalendarChange"
												@change="dateRangeChange">
												<template slot="renderExtraFooter"></template>
											</a-range-picker>
										</div>
									</div>
									<div class="row form-group">
										<div class="col-md-12">
											<label class="required">{{ $t('reportClassAttendance.classOperation')
											}}</label>
											<a-select v-model="model.class_id" :options="classOptions"
												:allowClear="true" :placeholder="$t('all')">
											</a-select>
											<div class="validate" v-if="errors.has('class_id')">
												{{ errors.first('class_id') }}
											</div>
										</div>
									</div>
									<div class="row form-group">
										<div class="col-md-12">
											<label class="required">{{
												$t('reportClassAttendance.session')
											}}</label>
											<a-select style="width: 100%" v-model="model.session_id"
												:options="sessionList" :show-search="true" :disabled="!model.class_id"
												:filter-option="(input, option) =>
													option.componentOptions.children[0].text
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
													">
											</a-select>
											<div class="validate" v-if="errors.has('session_id')">
												{{ errors.first('session_id') }}
											</div>
										</div>
									</div>
									<div class="row">
										<div class="col-md-6 text-right">
											<ts-button class="btn-block" outline color="success"
												@click.prevent="exportExcel" :waiting="exporting">{{
													$t('excel') }}</ts-button>
										</div>
										<div class="col-md-6 text-right">
											<ts-button class="btn-block" color="primary" @click.prevent="preview"
												:waiting="waiting">{{
													$t('preview') }}</ts-button>
										</div>
									</div>
								</fieldset>
							</form>
						</ts-loading-banner>
					</ts-card-wrapper>
				</ts-card>
			</div>
			<div class="col-md-9">
				<ts-card>
					<ts-card-wrapper>
						<form>
							<fieldset>
								<legend>{{ $t('reportClassAttendance.preview') }}</legend>
								<ts-preview-pdf v-model="waiting" :src="src" />
							</fieldset>
						</form>
					</ts-card-wrapper>
				</ts-card>
			</div>
		</div>
	</div>
</template>
<script>
import { mapActions } from 'vuex'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'
import moment from 'moment'
import { debounce } from 'lodash'

// Returns the first argument that is not null/undefined/empty-string.
// Used instead of the `??` (nullish coalescing) operator, which isn't
// enabled in this project's Babel config.
function firstDefined() {
	for (var i = 0; i < arguments.length; i++) {
		var val = arguments[i]
		if (val !== null && val !== undefined && val !== '') {
			return val
		}
	}
	return null
}

export default {
	name: 'reportClassAttendance',
	data() {
		return {
			loading: false,
			waiting: false,
			exporting: false,
			src: '',
			errors: new Errors(),
			sub_programs: [],
			ac_terms: [],
			ac_year_list: [],
			headers: [],
			columnLists: [],
			classListForTerm: [],
			sessionList: [],
			rangeDate: [moment().startOf('month'), moment().endOf('month')],
			selectingStartDate: null,

			// term boundaries used to constrain the date range picker
			termStartDate: null,
			termEndDate: null,

			model: {
				acps_id: null,
				ac_term_id: null,
				ac_year_id: null,
				session_id: null,
				class_id: null,
			}
		}
	},
	created() {
		this.fetchResource({
			fnName: 'branch,acYear,subProgram,term,getClass,acStatus,sessoin,studyTime'
		})
		this.fetchData()
		this.getHeader()
	},
	computed: {
		classOptions() {
			return (this.classListForTerm || [])
				.map(el => ({
					value: el.class_id,
					label:
						el.class_name +
						(el.teacher ? ' | ' + el.teacher.full_name_en : '') +
						(el.academic_study_time ? ' | ' + el.academic_study_time.study_time : '')
				}))
				.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
		}
	},
	methods: {
		...mapActions('reportAcademic', [
			'classAttendance',
			'getSubProgram',
			'getTerm',
			'getYear',
			'getClass',
			'formViewModel'
		]),
		moment,
		async fetchData() {
			this.loading = true
			await this.getYear()
				.then(response => {
					this.ac_year_list = response.data.map(el => ({
						value: el.ac_year_id,
						label: el.ac_year +
							(el.ac_status_id == 1
								? ' ( Current ) '
								: el.ac_status_id == 2
									? ' ( History ) '
									: ' ( Next ) ')
					}))
					// intentionally NOT alphabetically sorted — order reflects
					// Current/History/Next status, not label text
				})
				.catch(error => {
					this.$toasted.error(error.message)
				})
				.finally(() => {
					this.loading = false
				})
			await this.getSubProgram()
				.then(response => {
					this.sub_programs = response.data
						.map(el => ({
							value: el.acps_id,
							label: el.acps_name_foreign
						}))
						.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
				})
				.catch(error => {
					this.$toasted.error(error.message)
				})
				.finally(() => {
					this.loading = false
				})
		},
		async fetchResource(attributes) {
			this.loading = true
			let response = await this.formViewModel({
				params: attributes
			})
			if (attributes.fnName) {
				attributes.fnName.split(',').forEach(fn => {
					this[fn] = response[fn]
				});
			}
			this.loading = false
		},
		getHeader() {
			this.loading = true
			this.errors = new Errors()
			this.$store
				.dispatch('reportAcademic/getHeader', this.model)
				.then(response => {
					this.headers = response['columnHeaders']
					this.columnLists = response['columnLists']
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					this.$toasted.error(error.message)
					this.errors = new Errors(error.errors)
				})
		},
		onSubChange(acps_id) {
			this.model.ac_term_id = null
			this.model.class_id = null
			this.termStartDate = null
			this.termEndDate = null
			this.loading = true
			this.getTerm({
				filter: {
					acps_id: acps_id,
					ac_year_id: this.model.ac_year_id,
				},
			})
				.then(response => {
					this.ac_terms = response.data
						.map(el => {
							// FIX: confirmed via console log — this API returns the term's
							// date boundaries as `term_start` / `term_stop` (not `term_end`),
							// formatted as DD-MM-YYYY (e.g. "21-01-2026").
							const startRaw = firstDefined(
								el.start_date,
								el.ac_term_start_date,
								el.term_start_date,
								el.term_start
							)
							const endRaw = firstDefined(
								el.end_date,
								el.ac_term_end_date,
								el.term_end_date,
								el.term_stop,
								el.term_end
							)

							return {
								// FIX: force numeric value so the strict `===` lookup in
								// acTermChange (find by ac_term_id) can't silently fail due to
								// a string/number type mismatch coming from the API.
								value: Number(el.ac_term_id),
								label:
									el.term_name +
									(el.ac_status_id == 1
										? ' ( Current ) '
										: el.ac_status_id == 2
											? ' ( History ) '
											: ' ( Next ) '),
								start_date: startRaw,
								end_date: endRaw
							}
						})
						.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
				})
				.catch(error => {
					this.$toasted.error(error.message)
				})
				.finally(() => {
					this.loading = false
				})
		},
		acYearChange() {
			this.model.acps_id = null
			this.model.ac_term_id = null
			this.model.class_id = null
			this.termStartDate = null
			this.termEndDate = null
		},
		acTermChange(ac_term_id) {
			this.loading = true
			this.model.class_id = null
			this.model.session_id = null
			this.sessionList = []
			this.classListForTerm = []

			// FIX: compare as numbers so this never fails to match due to
			// string vs number type mismatch between the select's emitted
			// value and the stored term list.
			const selectedTerm = this.ac_terms.find(
				el => Number(el.value) === Number(ac_term_id)
			)

			const parsedStart = selectedTerm && selectedTerm.start_date
				? moment(selectedTerm.start_date, ['DD-MM-YYYY', 'YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601])
				: null
			const parsedEnd = selectedTerm && selectedTerm.end_date
				? moment(selectedTerm.end_date, ['DD-MM-YYYY', 'YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601])
				: null

			if (parsedStart && parsedStart.isValid() && parsedEnd && parsedEnd.isValid()) {
				this.termStartDate = parsedStart
				this.termEndDate = parsedEnd

				const today = moment().startOf('day')

				// default the picker to the "next" period relative to today:
				// - if today falls within the term, start from today
				// - otherwise (today is before or after the term), start from the term's start date
				let start
				if (
					today.isSameOrAfter(this.termStartDate, 'day') &&
					today.isSameOrBefore(this.termEndDate, 'day')
				) {
					start = today.clone()
				} else {
					start = this.termStartDate.clone()
				}

				// end is capped at 1 month out or the term's end date, whichever comes first
				let end = start.clone().add(1, 'months')
				if (end.isAfter(this.termEndDate)) {
					end = this.termEndDate.clone()
				}

				this.rangeDate = [start, end]
				this.dateRangeChange(this.rangeDate)
			} else {
				// FIX: surface the failure instead of silently disabling nothing.
				// If this fires in the console, the API response for this term
				// is missing/misnamed date fields — check the payload from getTerm.
				console.warn(
					'[reportClassAttendance] Could not resolve term start/end date for ac_term_id=',
					ac_term_id,
					'matched term:',
					selectedTerm
				)
				this.termStartDate = null
				this.termEndDate = null
				this.rangeDate = [null, null]
			}

			this.formViewModel({
				params: {
					fnName: 'getClass',
					acps_id: this.model.acps_id,
					ac_term_id: ac_term_id,
				}
			})
				.then(response => {
					this.classListForTerm = response.getClass || []
				})
				.catch(error => {
					this.$toasted.error(error.message)
				})
				.finally(() => {
					this.loading = false
				})
		},
		onCalendarChange(dates) {
			this.selectingStartDate = dates && dates[0] ? dates[0] : null
		},
		disabledDate(current) {
			if (!current) {
				return false
			}

			// hard limit: never allow dates outside the selected term's range
			if (this.termStartDate && current.isBefore(this.termStartDate, 'day')) {
				return true
			}
			if (this.termEndDate && current.isAfter(this.termEndDate, 'day')) {
				return true
			}

			// existing rule: only allow picking within 1 month of the first-selected date
			if (!this.selectingStartDate) {
				return false
			}
			return (
				current.isAfter(this.selectingStartDate.clone().add(1, 'months')) ||
				current.isBefore(this.selectingStartDate.clone().subtract(1, 'months'))
			)
		},
		dateRangeChange(dateRange) {
			this.selectingStartDate = null

			if (!dateRange || !dateRange[0] || !dateRange[1]) {
				return
			}

			const start = dateRange[0]
			const end = dateRange[1]
			this.rangeDate = dateRange
			this.$store.commit('reportAcademic/SET_START_DATE', start.format('DD-MM-YYYY'))
			this.$store.commit('reportAcademic/SET_END_DATE', end.format('DD-MM-YYYY'))
		},
		buildSessionList(selectedClassId) {
			// always clear session when class changes
			this.model.session_id = null

			const selected = this.classListForTerm.find(el => el.class_id === selectedClassId)

			const sessions = []
			if (selected) {
				if (selected.is_session1 == 1) {
					sessions.push({ value: 1, label: 'Session 1' })
				}
				if (selected.is_session2 == 1) {
					sessions.push({ value: 2, label: 'Session 2' })
				}
			}

			this.sessionList = sessions
		},
		preview() {
			this.waiting = true
			this.src = ''
			this.errors = new Errors()
			this.classAttendance(this.model,)
				.then(response => {
					this.src = response.url
				})
				.catch(error => {
					this.$toasted.error(error.message)
					this.errors = new Errors(error.errors)
				})
				.finally(() => {
					this.waiting = false
				})
		},
		exportExcel() {
			this.exporting = true
			this.errors = new Errors()

			this.$store
				.dispatch('reportAcademic/classAttendanceExcel', this.model)
				.then(response => {
					let filename = trim(
						response.headers['content-disposition']
							.substring(
								response.headers['content-disposition'].indexOf('filename')
							)
							.replace('filename=', ''),
						'"'
					)
					FileDownload(response.data, filename)
				})
				.catch(err => {
					let error = JSON.parse(
						String.fromCharCode.apply(null, new Uint8Array(err))
					)
					this.$toasted.error(error.message)
					this.errors = new Errors(error.errors)
				})
				.finally(() => (this.exporting = false))
		}
	},
	watch: {
		model: debounce(function (value) {
			this.getHeader()
		}, 500),
		'model.class_id'(newVal) {
			this.buildSessionList(newVal)
		}
	}
}
</script>
